import { Box, Container, Typography } from '@mui/material'
import React, { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { ROUTES } from '../../../helpers/routes-helper'
import RestaurantCardContainer from '../../../components/restaurant/RestaurantCardContainer'
import useAppUtilities from '../../../hooks/useAppUtilities'
import { useDevice } from '../../../hooks/useDevice'
import { useAppSelector } from '../../../store'
import { selectLocalList, selectIsLoading, getLandingPageDetails } from '../../../store/landingSlice'
import { selectLocation } from '../../../store/authSlice'
import { selectRadius } from '../../../store/restaurantSlice'
import { ILandingDetailsRequest, LandingType } from '../../../types'
import { type MenuSaveResponse } from '../../../api/menuScanApi'
import { Helmet } from 'react-helmet'
import {
  SaveConfirmationCard,
  SaveConfirmationTitle,
  SaveConfirmationMessage,
  SaveConfirmationSecondaryMessage,
  OpenInAppButton,
  BackToHomeButton,
  RestaurantsSection,
} from './styled/MenuSaveSuccess.styled'

interface LocationState {
  saveResponse?: MenuSaveResponse
  restaurantId?: string
}

const MenuSaveSuccess: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { theme, dispatch } = useAppUtilities()
  const { isHybrid } = useDevice()
  const { isAuthenticated } = useAuth0()
  const localList = useAppSelector(selectLocalList)
  const loading = useAppSelector(selectIsLoading)
  const userLocation = useAppSelector(selectLocation)
  const radius = useAppSelector(selectRadius)

  const state = location.state as LocationState | undefined
  const saveResponse = state?.saveResponse

  // Use same source as Home: fetch landing (Local Flavors) if not yet loaded
  useEffect(() => {
    if (localList != null) return
    if (userLocation?.latitude == null || userLocation?.longitude == null) return
    const request: ILandingDetailsRequest = {
      latitude: userLocation.latitude,
      longitude: userLocation.longitude,
      radius,
    }
    void dispatch(getLandingPageDetails(request))
  }, [dispatch, localList, userLocation?.latitude, userLocation?.longitude, radius])

  const restaurantList = localList ?? {
    name: 'Local Flavors To Explore',
    type: LandingType.LOCAL,
    restaurants: [],
  }
  const hasRestaurants = (restaurantList.restaurants?.length ?? 0) > 0
  const listFailed = !loading && localList == null && userLocation != null

  const handleOpenInApp = (): void => {
    // Deep link to mobile app
    // This will open the app if installed, otherwise redirect to app store
    const appScheme = 'resactly://'
    const fallbackUrl = 'https://resactly.com/download' // Update with actual app store link

    window.location.href = appScheme + ROUTES.MENU_SAVE_SUCCESS

    // Fallback to app store if app is not installed
    setTimeout(() => {
      window.location.href = fallbackUrl
    }, 2000)
  }

  const handleBackToHome = (): void => {
    navigate(ROUTES.HOME)
  }

  // Helper to generate points message
  const getPointsMessage = (): string | null => {
    if (!saveResponse) return null
    const { pointsGranted, pointsReason, newDishesAddedCount } = saveResponse

    if (pointsGranted === 0 || pointsReason === 'NONE') {
      if (newDishesAddedCount === 0) {
        return 'All dishes were already on the shared menu.'
      }
      if (newDishesAddedCount < 3) {
        return `You added ${newDishesAddedCount} dish${newDishesAddedCount > 1 ? 'es' : ''}. Add at least 3 new dishes next time to earn points!`
      }
      return null
    }

    if (pointsReason === 'FIRST_MENU_SCAN') {
      return `+${pointsGranted} points earned! You created the first "User Provided" menu for this restaurant.`
    }

    if (pointsReason === 'SUBSEQUENT_MENU_SAVE') {
      return `+${pointsGranted} points earned! You added ${newDishesAddedCount} new dish${newDishesAddedCount > 1 ? 'es' : ''} to the shared menu.`
    }

    return null
  }

  // Helper to generate dish counts summary
  const getDishCountsSummary = (): string | null => {
    if (!saveResponse) return null
    const { newDishesAddedCount, dedupedCount } = saveResponse

    if (newDishesAddedCount === 0 && dedupedCount === 0) {
      return null
    }

    if (dedupedCount === 0) {
      return `${newDishesAddedCount} new dish${newDishesAddedCount > 1 ? 'es' : ''} added`
    }

    return `${newDishesAddedCount} new dish${newDishesAddedCount > 1 ? 'es' : ''} added, ${dedupedCount} duplicate${dedupedCount > 1 ? 's' : ''} skipped`
  }

  const pointsMessage = getPointsMessage()
  const dishCountsSummary = getDishCountsSummary()
  const publishConfirmation = saveResponse?.published && saveResponse?.publishMessage 
    ? saveResponse.publishMessage 
    : null

  return (
    <Box sx={{ backgroundColor: theme.palette.grey[50], minHeight: '100vh' }}>
      <Helmet>
        <title>Resactly | Menu Saved</title>
      </Helmet>
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Save Confirmation Card */}
        <SaveConfirmationCard>
          <SaveConfirmationTitle variant="h4">Menu saved</SaveConfirmationTitle>
          <SaveConfirmationMessage variant="body1">
            {saveResponse?.message ?? 'You can come back to this menu later and receive updated picks.'}
          </SaveConfirmationMessage>
          
          {/* Publish Confirmation */}
          {publishConfirmation && (
            <SaveConfirmationSecondaryMessage 
              variant="body2"
              sx={{ 
                fontWeight: 500,
                color: 'success.main'
              }}
            >
              {publishConfirmation}
            </SaveConfirmationSecondaryMessage>
          )}

          {/* Points Message */}
          {pointsMessage && (
            <SaveConfirmationSecondaryMessage 
              variant="body2"
              sx={{ 
                fontWeight: saveResponse?.pointsGranted && saveResponse.pointsGranted > 0 ? 600 : 400,
                color: saveResponse?.pointsGranted && saveResponse.pointsGranted > 0 
                  ? 'success.main' 
                  : 'text.secondary'
              }}
            >
              {pointsMessage}
            </SaveConfirmationSecondaryMessage>
          )}

          {/* Dish Counts Summary */}
          {dishCountsSummary && (
            <SaveConfirmationSecondaryMessage variant="body2">
              {dishCountsSummary}
            </SaveConfirmationSecondaryMessage>
          )}

          {!isAuthenticated && !isHybrid && (
            <OpenInAppButton variant="contained" onClick={handleOpenInApp}>
              Open in the app
            </OpenInAppButton>
          )}
        </SaveConfirmationCard>

        {/* Local Flavors (same source as Home) */}
        <RestaurantsSection>
          <RestaurantCardContainer
            title={restaurantList.name ?? 'Local Flavors To Explore'}
            cardList={restaurantList}
            loading={loading && localList == null}
            name={LandingType.LOCAL}
            isHybrid={isHybrid}
          />
          {listFailed && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              Could not load recommendations. Check back later or head home to explore.
            </Typography>
          )}
          {!listFailed && !loading && !hasRestaurants && localList != null && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              No local spots right now. Head home to discover more.
            </Typography>
          )}
        </RestaurantsSection>

        {/* Back to Home Button */}
        <BackToHomeButton variant="outlined" onClick={handleBackToHome}>
          Back to Home
        </BackToHomeButton>
      </Container>
    </Box>
  )
}

export default MenuSaveSuccess
